import React, { useEffect, useState } from 'react';
import {
  Avatar, Box, Button, HStack, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalHeader, ModalOverlay, Text, VStack, useToast,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { BlockedPlayer, subscribeToBlocks, unblockUser } from '../../services/blockService';
import { POKER_COLORS } from '../../theme/designSystem';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Players you have blocked, with an Unblock button for each. Blocking
 * happens from the table (voice bar or chat) or a public profile; this is
 * the one place to undo it.
 */
const BlockedPlayersModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [blocks, setBlocks] = useState<BlockedPlayer[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !currentUser?.uid) return;
    return subscribeToBlocks(currentUser.uid, setBlocks);
  }, [isOpen, currentUser?.uid]);

  const handleUnblock = async (b: BlockedPlayer) => {
    setBusy(b.targetUserId);
    const r = await unblockUser(b.targetUserId);
    setBusy(null);
    if (r.success) {
      toast({ title: `${b.username} unblocked`, status: 'success', duration: 3000 });
    } else {
      toast({ title: 'Could not unblock', description: r.error, status: 'error', duration: 4000 });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg={POKER_COLORS.darkTeal} border="1px solid" borderColor="whiteAlpha.200">
        <ModalHeader color="white">Blocked players</ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody pb={6}>
          <Text color="gray.400" fontSize="sm" mb={4}>
            Blocked players are muted and hidden for you at every table, their table chat is hidden, and neither of you can message the other.
          </Text>
          {blocks.length === 0 ? (
            <Box py={8} textAlign="center">
              <Text color="gray.500">You have not blocked anyone.</Text>
            </Box>
          ) : (
            <VStack spacing={2} align="stretch">
              {blocks.map((b) => (
                <HStack
                  key={b.targetUserId}
                  justify="space-between"
                  p={3}
                  bg="whiteAlpha.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                >
                  <HStack spacing={3}>
                    <Avatar size="sm" name={b.username} src={b.photoURL || undefined} />
                    <Text color="white" fontWeight="semibold">{b.username}</Text>
                  </HStack>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="cyan"
                    isLoading={busy === b.targetUserId}
                    onClick={() => handleUnblock(b)}
                  >
                    Unblock
                  </Button>
                </HStack>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BlockedPlayersModal;
